import React, { useState, useRef, useEffect } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setAuthenticated } from "../../store/authSlice";
import { API_BASE_URL } from '../../config';

const QRScanner = () => {
  const [error, setError] = useState(null);
  const [scanning, setScanning] = useState(true);
  const [showPasswordInput, setShowPasswordInput] = useState(false);
  const [password, setPassword] = useState("");
  const [scannedUserId, setScannedUserId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const scannerRef = useRef(null);
  const containerRef = useRef(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const onScanSuccess = async (decodedText) => {
    try {
      console.log("QR Code decoded:", decodedText);

      // Stop scanner
      if (scannerRef.current && scannerRef.current.isScanning) {
        await scannerRef.current.stop();
      }

      console.log("Sending data to acceptQR:", decodedText);
      const response = await fetch(`${API_BASE_URL}/api/Account/acceptQR`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          userId: decodedText,
        }),
      });

      const responseData = await response.json();
      console.log("Raw response:", responseData);

      if (!response.ok) {
        throw new Error(responseData.message || "Failed to verify QR code");
      }

      // Store just the userId, not the entire response object
      setScannedUserId(responseData.userId);
      setScanning(false);
      setShowPasswordInput(true);
    } catch (error) {
      console.error("QR code error:", error);
      setError(error.message);
      // Restart scanner on error
      if (scannerRef.current) {
        scannerRef.current.resume();
      }
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    console.log("Attempting login with:", { userId: scannedUserId, password });

    try {
      const response = await fetch(`${API_BASE_URL}/api/Account/loginQR`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          userId: scannedUserId,
          password: password,
        }),
      });

      const data = await response.json();
      console.log("Login response:", data);

      if (!response.ok) {
        console.log("Login error response:", data);
        throw new Error(data.message || "Login failed. Please try again.");
      }

      // Parse JWT token to get user info
      const tokenParts = data.token.split(".");
      const tokenPayload = JSON.parse(atob(tokenParts[1]));

      // Dispatch with same structure as traditional login
      dispatch(
        setAuthenticated({
          token: data.token,
          roles: data.roles,
          allowedEndpoints: data.allowedEndpoints,
          username: tokenPayload.unique_name,
          email: tokenPayload.email,
        })
      );

      navigate("/dashboard");
    } catch (err) {
      console.error("Login error:", err);
      setError(err.message);
      setPassword("");
    } finally {
      setIsLoading(false);
    }
  };

  const handleRetry = () => {
    setError(null);
    setScanning(true);
  };

  useEffect(() => {
    let isComponentMounted = true;

    const initializeScanner = async () => {
      try {
        if (!containerRef.current) return;

        // First request camera permission explicitly
        await navigator.mediaDevices.getUserMedia({ video: true });

        scannerRef.current = new Html5Qrcode("qr-reader");

        if (!scanning || !isComponentMounted) return;

        await scannerRef.current.start(
          { facingMode: "environment" },
          {
            fps: 10,
            qrbox: { width: 250, height: 250 },
          },
          onScanSuccess,
          (errorMessage) => {
            if (!isComponentMounted) return;
            console.error(`Scan error: ${errorMessage}`);
          }
        );
      } catch (err) {
        if (!isComponentMounted) return;
        if (err.name === "NotAllowedError") {
          setError(
            "Camera access was denied. Please allow camera access in your browser settings."
          );
        } else {
          setError("Failed to start camera. Please check permissions.");
        }
        console.error("Scanner initialization error:", err);
      }
    };

    const timeoutId = setTimeout(initializeScanner, 100);

    return () => {
      isComponentMounted = false;
      clearTimeout(timeoutId);
      if (scannerRef.current) {
        if (scannerRef.current.isScanning) {
          scannerRef.current.stop().catch(() => {
            // Ignore cleanup errors
          });
        }
      }
    };
  }, [scanning]);

  return (
    <div className="flex flex-col items-center space-y-4">
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative">
          <span className="block sm:inline">{error}</span>
          <button
            onClick={handleRetry}
            className="text-red-500 hover:text-red-700 font-medium underline ml-2"
          >
            Try Again
          </button>
        </div>
      )}

      {showPasswordInput ? (
        <form onSubmit={handlePasswordSubmit} className="w-full max-w-md">
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2">
              Enter Password
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              placeholder="Password"
              required
            />
          </div>
          <button
            type="submit"
            disabled={isLoading}
            className={`w-full bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline ${
              isLoading ? "opacity-50 cursor-not-allowed" : ""
            }`}
          >
            {isLoading ? "Logging in..." : "Login"}
          </button>
        </form>
      ) : (
        <div
          ref={containerRef}
          className="w-full max-w-md bg-white p-4 rounded-lg shadow-md"
        >
          <div id="qr-reader" className="w-full"></div>
          <p className="text-center text-gray-600 mt-4">
            Position the QR code within the frame to scan
          </p>
        </div>
      )}
    </div>
  );
};

export default QRScanner;
